import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { USER_TYPES, formatUserTypeLabel, normalizeUserType } from '../constants/userTypes';
import { FULL_TIME_PRODUCT_TYPE, productTypeOptions } from '../constants/productTypes';
import { getBackendBaseUrl, getContractApiUrl } from '../utils/apiUrls';
import { billingAuthHeaders } from '../utils/billingApi';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

function DeveloperUsers() {
    const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const navigate = useNavigate();

    const [pageSize, setPageSize] = useState(20);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [offset, setOffset] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [email, setEmail] = useState('');
    const [brokerageCompanyName, setBrokerageCompanyName] = useState('');
    const [userType, setUserType] = useState('');
    const [createdAtStart, setCreatedAtStart] = useState('');
    const [createdAtEnd, setCreatedAtEnd] = useState('');

    // Create User State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const EMPTY_CREATE_FORM = {
        name: '',
        email: '',
        user_type: 'real_estate_agent',
        brokerage_company_name: '',
        phone_number: '',
        licensed_state: '',
        license_number: '',
        company_address: '',
        company_license_number: '',
        is_paying_subscriber: false,
        payment_user_type: '',
        product_type: FULL_TIME_PRODUCT_TYPE,
        payment_method_status: 'none',
        is_super_admin: false,
        email_behavior_mode: 'forwarding',
        profile_completed: false,
        send_welcome_email: false,
    };
    const [createFormData, setCreateFormData] = useState(EMPTY_CREATE_FORM);

    const resetCreateForm = () => setCreateFormData({ ...EMPTY_CREATE_FORM });

    const handleCreateUser = async () => {
        if (!createFormData.name || !createFormData.email || !createFormData.user_type) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setCreateLoading(true);
            const payload = {
                name: createFormData.name.trim(),
                email: createFormData.email.trim(),
                user_type: createFormData.user_type,
                brokerage_company_name: createFormData.brokerage_company_name.trim() || undefined,
                phone_number: createFormData.phone_number.trim() || undefined,
                licensed_state: createFormData.licensed_state.trim().toUpperCase() || undefined,
                license_number: createFormData.license_number.trim() || undefined,
                company_address: createFormData.company_address.trim() || undefined,
                company_license_number: createFormData.company_license_number.trim() || undefined,
                is_paying_subscriber: createFormData.is_paying_subscriber,
                payment_user_type: createFormData.payment_user_type || undefined,
                product_type: createFormData.product_type || undefined,
                payment_method_status: createFormData.payment_method_status || 'none',
                is_super_admin: createFormData.is_super_admin,
                email_behavior_mode: createFormData.email_behavior_mode,
                profile_completed: createFormData.profile_completed,
                send_welcome_email: createFormData.send_welcome_email,
            };
            const response = await axios.post(`${BACKEND_URL}/users/create`, payload);

            if (response.data?.status === 'partial_success') {
                setShowCreateModal(false);
                resetCreateForm();
                fetchUsers(true);
                alert(response.data?.message || 'User created, but the welcome email failed to send.');
            } else if (response.data?.status === 'success') {
                setShowCreateModal(false);
                resetCreateForm();
                fetchUsers(true);
                alert(createFormData.send_welcome_email
                    ? 'User created and welcome email sent'
                    : 'User created successfully');
            } else {
                throw new Error(response.data?.error || 'Failed to create user');
            }
        } catch (e) {
            alert(e.response?.data?.error || e.message || 'Failed to create user');
        } finally {
            setCreateLoading(false);
        }
    };

    // Edit User State
    const [showEditModal, setShowEditModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        user_type: '',
        brokerage_company_name: '',
        is_paying_subscriber: false,
        is_super_admin: false,
        phone_number: '',
        licensed_state: '',
        license_number: '',
        company_address: '',
        company_license_number: '',
        email_behavior_mode: 'forwarding',
        payment_user_type: '',
        product_type: FULL_TIME_PRODUCT_TYPE,
        payment_method_status: 'none',
        profile_completed: false,
    });

    const handleEditClick = (user) => {
        setEditUser(user);
        setEditFormData({
            name: user.name || '',
            email: user.email || '',
            user_type: user.user_type || '',
            brokerage_company_name: user.brokerage_company_name || '',
            is_paying_subscriber: user.is_paying_subscriber || false,
            is_super_admin: user.is_super_admin || false,
            phone_number: user.phone_number || '',
            licensed_state: user.licensed_state || '',
            license_number: user.license_number || '',
            company_address: user.company_address || '',
            company_license_number: user.company_license_number || '',
            email_behavior_mode: user.email_behavior_mode || 'forwarding',
            payment_user_type: user.payment_user_type || '',
            product_type: user.product_type || FULL_TIME_PRODUCT_TYPE,
            payment_method_status: user.payment_method_status || 'none',
            profile_completed: user.profile_completed || false,
        });
        setShowEditModal(true);
    };

    const handleUpdateUser = async () => {
        if (!editUser) return;

        try {
            setEditLoading(true);
            const response = await axios.put(`${BACKEND_URL}/users/${editUser.id}`, editFormData);

            if (response.data?.status === 'success') {
                alert('User updated successfully');
                setShowEditModal(false);
                setEditUser(null);
                fetchUsers(false); // Refresh list
            } else {
                throw new Error(response.data?.error || 'Failed to update user');
            }
        } catch (e) {
            alert(e.response?.data?.error || e.message || 'Failed to update user');
        } finally {
            setEditLoading(false);
        }
    };


    // Delete User State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const handleDeleteClick = (userId) => {
        setUserToDelete(userId);
        setShowDeleteModal(true);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setLoading(true);
            const response = await axios.delete(`${BACKEND_URL}/users/${userToDelete}`);

            if (response.data?.status === 'success') {
                alert('User deleted successfully');
                fetchUsers(false);
            } else {
                throw new Error(response.data?.error || 'Failed to delete user');
            }
        } catch (e) {
            alert(e.response?.data?.error || e.message || 'Failed to delete user');
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setUserToDelete(null);
        }
    };

    const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
    const [userToUnsubscribe, setUserToUnsubscribe] = useState(null);

    const canUnsubscribeUser = (item) => {
        if (!item) return false;
        if ((item.billing_status || '').toLowerCase() === 'canceled') return false;
        if (item.subscription_cancelled_at) return false;
        if (item.subscription_teardown_pending) return false;
        return Boolean(item.payment_user_type || item.stripe_subscription_id || item.is_paying_subscriber);
    };

    const handleUnsubscribeClick = (item) => {
        setUserToUnsubscribe(item);
        setShowUnsubscribeModal(true);
    };

    const confirmUnsubscribeUser = async () => {
        if (!userToUnsubscribe) return;
        try {
            setLoading(true);
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const response = await axios.post(
                `${getBackendBaseUrl()}/billing/admin/user/${userToUnsubscribe.id}/unsubscribe`,
                {},
                { headers },
            );
            if (response.data?.status === 'success') {
                alert(response.data.immediate
                    ? 'User unsubscribed. Access was removed immediately.'
                    : 'User unsubscribe scheduled for the end of the billing period.');
                fetchUsers(true);
            } else {
                throw new Error(response.data?.error || 'Failed to unsubscribe user');
            }
        } catch (e) {
            alert(e.response?.data?.error || e.message || 'Failed to unsubscribe user');
        } finally {
            setLoading(false);
            setShowUnsubscribeModal(false);
            setUserToUnsubscribe(null);
        }
    };

    const isKeyFloUser = useMemo(() => {
        const email = user?.email || '';
        const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
        return ALLOWED_USERS.includes(email);
    }, [user]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isKeyFloUser) {
            navigate('/');
        }
    }, [isAuthenticated, isKeyFloUser, navigate]);

    const formatDateTime = (ts) => {
        try {
            if (!ts) return '';
            return new Date(ts).toLocaleString();
        } catch (e) {
            return ts || '';
        }
    };

    const buildParams = (resetOffset = true) => {
        const params = {
            limit: pageSize,
            offset: resetOffset ? 0 : offset
        };

        if (email) params.email = email;
        if (brokerageCompanyName) params.brokerage_company_name = brokerageCompanyName;
        if (userType) params.user_type = userType;
        if (createdAtStart) params.created_at_start = new Date(createdAtStart).toISOString();
        if (createdAtEnd) {
            const end = new Date(createdAtEnd);
            end.setHours(23, 59, 59, 999);
            params.created_at_end = end.toISOString();
        }

        return params;
    };

    const fetchUsers = async (reset = true) => {
        if (!isAuthenticated || !isKeyFloUser) return;
        try {
            setLoading(true);
            setError('');

            const params = buildParams(reset);
            const response = await axios.get(`${BACKEND_URL}/users/dev`, { params });

            if (response.data?.status !== 'success') {
                throw new Error(response.data?.error || 'Failed to fetch users');
            }

            const newUsers = response.data.users || [];
            setUsers(reset ? newUsers : [...users, ...newUsers]);
            setTotalCount(response.data.total_count || 0);
            setOffset((reset ? 0 : offset) + newUsers.length);
        } catch (e) {
            setError(e.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && isKeyFloUser) fetchUsers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isKeyFloUser, pageSize]);

    // Get all column names from the first record (if available)
    const getDisplayColumns = () => {
        if (users.length === 0) return ['id', 'email', 'name', 'user_type', 'created_at', 'updated_at'];

        // Sort columns to put important ones first
        const firstUser = users[0];
        const allCols = Object.keys(firstUser).sort();

        const priorityCols = ['id', 'email', 'name', 'user_type', 'payment_user_type', 'product_type', 'payment_method_status', 'billing_status', 'is_paying_subscriber', 'is_super_admin', 'email_behavior_mode'];
        const otherCols = allCols.filter(c => !priorityCols.includes(c));

        return [...priorityCols, ...otherCols];
    };

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({
                    authorizationParams: {
                        prompt: 'login'
                    }
                })}>Log in</button>
            </div>
        );
    }

    if (!isKeyFloUser) {
        return (
            <div style={{ padding: '24px' }}>
                <h2>Access Denied</h2>
                <p>This page is restricted to authorized KeyFlo administrators.</p>
            </div>
        );
    }

    const displayColumns = getDisplayColumns();

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Users</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
                    <label style={{ marginRight: '8px' }}>Page size:</label>
                    <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <DeveloperNavigation />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Email</label>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Brokerage</label>
                    <input value={brokerageCompanyName} onChange={(e) => setBrokerageCompanyName(e.target.value)} placeholder="brokerage name" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User Type</label>
                    <input value={userType} onChange={(e) => setUserType(e.target.value)} placeholder="user_type" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Created Start</label>
                    <input type="date" value={createdAtStart} onChange={(e) => setCreatedAtStart(e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Created End</label>
                    <input type="date" value={createdAtEnd} onChange={(e) => setCreatedAtEnd(e.target.value)} style={{ width: '100%' }} />
                </div>
            </div>


            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => { setOffset(0); fetchUsers(true); }}>Apply Filters</button>
                <button onClick={() => {
                    setEmail('');
                    setBrokerageCompanyName('');
                    setUserType('');
                    setCreatedAtStart('');
                    setCreatedAtEnd('');
                    setOffset(0);
                    fetchUsers(true);
                }}>Clear</button>
                <button onClick={() => setShowCreateModal(true)} style={{ marginLeft: 'auto', backgroundColor: '#10b981', color: 'white', border: 'none' }}>+ User</button>
                <button onClick={() => fetchUsers(false)}>Load More</button>
            </div>

            {loading && <LoadingSpinner />}
            {error && (
                <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
            )}

            <div style={{ marginBottom: '12px' }}>
                Showing {users.length} of {totalCount} users
            </div>

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th style={{
                                textAlign: 'left',
                                padding: '10px 12px',
                                borderBottom: '1px solid #e5e7eb',
                                background: '#f8fafc',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                Actions
                            </th>
                            {displayColumns.map((col, colIndex) => (
                                <th
                                    key={col}
                                    style={{
                                        textAlign: 'left',
                                        padding: '10px 12px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f8fafc',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1
                                    }}
                                >
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((item, idx) => (
                            <tr key={item.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '60px' }}>
                                <td style={{
                                    padding: '10px 12px',
                                    borderBottom: '1px solid #f1f5f9',
                                    verticalAlign: 'top',
                                    display: 'flex',
                                    gap: '8px'
                                }}>
                                    <button
                                        onClick={() => handleEditClick(item)}
                                        style={{
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(item.id)}
                                        style={{
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Delete
                                    </button>
                                    <button
                                        onClick={() => handleUnsubscribeClick(item)}
                                        disabled={!canUnsubscribeUser(item)}
                                        style={{
                                            backgroundColor: canUnsubscribeUser(item) ? '#b91c1c' : '#d1d5db',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            cursor: canUnsubscribeUser(item) ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        Unsubscribe
                                    </button>
                                </td>
                                {displayColumns.map((col) => {
                                    let val = item[col];

                                    // Format dates
                                    if (col.includes('_at') || col.includes('date')) {
                                        val = formatDateTime(val);
                                    }

                                    // Format boolean values
                                    if (typeof val === 'boolean') {
                                        val = val ? 'true' : 'false';
                                    }

                                    if (col === 'user_type') {
                                        val = formatUserTypeLabel(val);
                                    }

                                    if (val === null || typeof val === 'undefined') val = '';

                                    return (
                                        <td
                                            key={col}
                                            style={{
                                                padding: '10px 12px',
                                                borderBottom: '1px solid #f1f5f9',
                                                borderRight: '1px solid #f1f5f9',
                                                verticalAlign: 'top',
                                                whiteSpace: 'pre-wrap',
                                                minWidth: 120,
                                                maxWidth: 300
                                            }}
                                        >
                                            <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                                                {String(val)}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {users.length === 0 && !loading && (
                            <tr>
                                <td colSpan={displayColumns.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No users found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        width: '520px',
                        maxWidth: '95%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                    }}>
                        <h3 style={{ marginTop: 0 }}>Create User</h3>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Name *</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.name}
                                onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Email *</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.email}
                                onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Phone</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.phone_number}
                                onChange={(e) => setCreateFormData({ ...createFormData, phone_number: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>User Type *</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.user_type}
                                onChange={(e) => setCreateFormData({ ...createFormData, user_type: e.target.value })}
                            >
                                {USER_TYPES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Brokerage / Company</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.brokerage_company_name}
                                onChange={(e) => setCreateFormData({ ...createFormData, brokerage_company_name: e.target.value })}
                            />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Licensed State</label>
                                <input
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                    value={createFormData.licensed_state}
                                    onChange={(e) => setCreateFormData({ ...createFormData, licensed_state: e.target.value.toUpperCase() })}
                                    placeholder="e.g. OK"
                                    maxLength={2}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>License Number</label>
                                <input
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                    value={createFormData.license_number}
                                    onChange={(e) => setCreateFormData({ ...createFormData, license_number: e.target.value })}
                                />
                            </div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Company Address</label>
                            <textarea
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '64px' }}
                                value={createFormData.company_address}
                                onChange={(e) => setCreateFormData({ ...createFormData, company_address: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Company License Number</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.company_license_number}
                                onChange={(e) => setCreateFormData({ ...createFormData, company_license_number: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Payment User Type</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.payment_user_type || ''}
                                onChange={(e) => setCreateFormData({ ...createFormData, payment_user_type: e.target.value })}
                            >
                                <option value="">—</option>
                                <option value="beta_tester">beta_tester</option>
                                <option value="free_trial">free_trial</option>
                                <option value="subscriber">subscriber</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Product Type</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.product_type || FULL_TIME_PRODUCT_TYPE}
                                onChange={(e) => setCreateFormData({ ...createFormData, product_type: e.target.value })}
                            >
                                {productTypeOptions(createFormData.product_type).map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Payment Method Status</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.payment_method_status || 'none'}
                                onChange={(e) => setCreateFormData({ ...createFormData, payment_method_status: e.target.value })}
                            >
                                <option value="none">none</option>
                                <option value="pending">pending</option>
                                <option value="verified">verified</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="createIsPayingSubscriber"
                                checked={createFormData.is_paying_subscriber}
                                onChange={(e) => setCreateFormData({ ...createFormData, is_paying_subscriber: e.target.checked })}
                            />
                            <label htmlFor="createIsPayingSubscriber" style={{ fontSize: '0.9rem' }}>Is Paying Subscriber?</label>
                        </div>
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="createIsSuperAdmin"
                                checked={createFormData.is_super_admin}
                                onChange={(e) => setCreateFormData({ ...createFormData, is_super_admin: e.target.checked })}
                            />
                            <label htmlFor="createIsSuperAdmin" style={{ fontSize: '0.9rem' }}>Is Super Admin?</label>
                        </div>
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="createProfileCompleted"
                                checked={createFormData.profile_completed}
                                onChange={(e) => setCreateFormData({ ...createFormData, profile_completed: e.target.checked })}
                            />
                            <label htmlFor="createProfileCompleted" style={{ fontSize: '0.9rem' }}>Profile Completed</label>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Email Behavior Mode</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={createFormData.email_behavior_mode}
                                onChange={(e) => setCreateFormData({ ...createFormData, email_behavior_mode: e.target.value })}
                            >
                                <option value="forwarding">Forwarding</option>
                                <option value="ask_first">Ask First</option>
                                <option value="autonomous">Autonomous</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="createSendWelcomeEmail"
                                checked={createFormData.send_welcome_email}
                                onChange={(e) => setCreateFormData({ ...createFormData, send_welcome_email: e.target.checked })}
                            />
                            <label htmlFor="createSendWelcomeEmail" style={{ fontSize: '0.9rem' }}>Send welcome / access-granted email</label>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button onClick={() => { setShowCreateModal(false); resetCreateForm(); }} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Cancel</button>
                            <button onClick={handleCreateUser} disabled={createLoading} style={{ background: '#10b981', border: 'none', color: 'white' }}>
                                {createLoading ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        width: '520px',
                        maxWidth: '95%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                    }}>
                        <h3 style={{ marginTop: 0 }}>Edit User</h3>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Name</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.name}
                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Email</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.email}
                                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>User Type</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={normalizeUserType(editFormData.user_type) || editFormData.user_type}
                                onChange={(e) => setEditFormData({ ...editFormData, user_type: e.target.value })}
                            >
                                {USER_TYPES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Brokerage / Company</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.brokerage_company_name}
                                onChange={(e) => setEditFormData({ ...editFormData, brokerage_company_name: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Phone</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.phone_number}
                                onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
                            />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Licensed State</label>
                                <input
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                    value={editFormData.licensed_state}
                                    onChange={(e) => setEditFormData({ ...editFormData, licensed_state: e.target.value.toUpperCase() })}
                                    placeholder="e.g. OK"
                                    maxLength={2}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>License Number</label>
                                <input
                                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                    value={editFormData.license_number}
                                    onChange={(e) => setEditFormData({ ...editFormData, license_number: e.target.value })}
                                />
                            </div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Company Address</label>
                            <textarea
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '64px' }}
                                value={editFormData.company_address}
                                onChange={(e) => setEditFormData({ ...editFormData, company_address: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Company License Number</label>
                            <input
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.company_license_number}
                                onChange={(e) => setEditFormData({ ...editFormData, company_license_number: e.target.value })}
                            />
                        </div>
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="editIsPayingSubscriber"
                                checked={editFormData.is_paying_subscriber}
                                onChange={(e) => setEditFormData({ ...editFormData, is_paying_subscriber: e.target.checked })}
                            />
                            <label htmlFor="editIsPayingSubscriber" style={{ fontSize: '0.9rem' }}>Is Paying Subscriber?</label>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Payment User Type</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.payment_user_type || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, payment_user_type: e.target.value })}
                            >
                                <option value="">—</option>
                                <option value="beta_tester">beta_tester</option>
                                <option value="free_trial">free_trial</option>
                                <option value="subscriber">subscriber</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Product Type</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.product_type || FULL_TIME_PRODUCT_TYPE}
                                onChange={(e) => setEditFormData({ ...editFormData, product_type: e.target.value })}
                            >
                                {productTypeOptions(editFormData.product_type).map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Payment Method Status</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.payment_method_status || 'none'}
                                onChange={(e) => setEditFormData({ ...editFormData, payment_method_status: e.target.value })}
                            >
                                <option value="none">none</option>
                                <option value="pending">pending</option>
                                <option value="verified">verified</option>
                            </select>
                        </div>
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="editIsSuperAdmin"
                                checked={editFormData.is_super_admin}
                                onChange={(e) => setEditFormData({ ...editFormData, is_super_admin: e.target.checked })}
                            />
                            <label htmlFor="editIsSuperAdmin" style={{ fontSize: '0.9rem' }}>Is Super Admin?</label>
                        </div>
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="editProfileCompleted"
                                checked={editFormData.profile_completed}
                                onChange={(e) => setEditFormData({ ...editFormData, profile_completed: e.target.checked })}
                            />
                            <label htmlFor="editProfileCompleted" style={{ fontSize: '0.9rem' }}>Profile Completed</label>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '4px' }}>Email Behavior Mode</label>
                            <select
                                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                value={editFormData.email_behavior_mode}
                                onChange={(e) => setEditFormData({ ...editFormData, email_behavior_mode: e.target.value })}
                            >
                                <option value="forwarding">Forwarding</option>
                                <option value="ask_first">Ask First</option>
                                <option value="autonomous">Autonomous</option>
                            </select>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button onClick={() => setShowEditModal(false)} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151' }}>Cancel</button>
                            <button onClick={handleUpdateUser} disabled={editLoading} style={{ background: '#3b82f6', border: 'none', color: 'white' }}>
                                {editLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showUnsubscribeModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '440px', maxWidth: '90%' }}>
                        <h3 style={{ marginTop: 0, color: '#b91c1c' }}>Confirm Unsubscribe</h3>
                        <p>
                            This is the same as the user canceling themselves.
                            Paid subscribers keep access until the period ends, then full teardown.
                            Free-trial users with no charges are torn down immediately.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                            <button
                                onClick={() => { setShowUnsubscribeModal(false); setUserToUnsubscribe(null); }}
                                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmUnsubscribeUser}
                                disabled={loading}
                                style={{ background: '#b91c1c', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                {loading ? 'Unsubscribing...' : 'Unsubscribe User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
                        <h3 style={{ marginTop: 0, color: '#ef4444' }}>Confirm Delete</h3>
                        <p>Are you sure you want to delete this user? This action cannot be undone.</p>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                            <button
                                onClick={() => { setShowDeleteModal(false); setUserToDelete(null); }}
                                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteUser}
                                disabled={loading}
                                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
                            >
                                {loading ? 'Deleting...' : 'Delete User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DeveloperUsers;
